import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { rtdb } from "../firebase";
import { ref, get } from "firebase/database"; 
import "./AdminProgress.css";
import FGItemCard from "./FGItemCard";
import FGDateHeader from "../FgDateHeader";
import { useProgress } from "../pages/ProgressContext";

// Moved static array configuration outside component scope to maintain static reference integrity
const NODES = ["Receive", "Treat", "Paint", "Pack", "Deliver"];

function AdminProgress({ cachedData, setCachedData, hasLoadedOnce, setHasLoadedOnce, selectedFG, setSelectedFG, onNavigate, onRegisterAction }) {
  const {
    fgItems = [], setFgItems = () => {},
    nestedData = {}, setNestedData = () => {},
    expandedKeys = new Set(), setExpandedKeys = () => {},
    searchQuery = "", setSearchQuery = () => {},
    selectedLeaf = null, setSelectedLeaf = () => {},
    activeKey = "", setActiveKey = () => {},
    loading = false, setLoading = () => {},
    loadingOlder = false, setLoadingOlder = () => {},
    forceExpandCard,         
    clearExpandedUIStates,
    sortLatest = true
  } = useProgress() || {};

  const [localHighlightTarget, setLocalHighlightTarget] = useState(null);
  const cacheRef = useRef(null);

  const getFullTimestamp = (d, t) => {
    if (!d) return 0;
    const [day, month, year] = d.split("/").map(Number);
    let hour = 0, minute = 0;
    if (t) {
      const time = t.toLowerCase();
      const isAfternoon = time.includes("pm") || time.includes("ptg") || time.includes("pt") || time.includes("mlm");
      const parts = time.replace(/[^\d:]/g, "").split(":");
      hour = parseInt(parts[0], 10);
      minute = parseInt(parts[1] || "0", 10);
      if (isAfternoon && hour !== 12) hour += 12;
      if (!isAfternoon && hour === 12) hour = 0;
    }
    return new Date(year, month - 1, day, hour, minute).getTime();
  };

  const compiledIssues = useMemo(() => {
    const allIssues = [];

    fgItems.forEach((item) => {
      if (!item || !item.nodeRemarks) return;

      // Cross-check all 5 manufacturing workstation nodes
      NODES.forEach((nodeName) => {
        const stageData = item.nodeRemarks[nodeName];

        // Verify the issue child and its text message exist
        if (stageData && stageData.issue && stageData.issue.message) {
          allIssues.push({
            id: `${item.fgNumber}-${nodeName}-${stageData.issue.reportedTime || Date.now()}`,
            fgNumber: item.fgNumber,
            station: nodeName,
            message: stageData.issue.message,
            reportedBy: stageData.issue.reportedBy || "System",
            reportedTime: stageData.issue.reportedTime || null,
            isUnread: stageData.issue.status === "open",
            status: stageData.issue.status || "closed" // Keeps "open" or "closed" strings intact
          });
        }
      });
    });

    // Sort by newest reported time first so your notifications are chronological
    return allIssues.sort((a, b) => (b.reportedTime || 0) - (a.reportedTime || 0));
  }, [fgItems]);

  // Register the issues data payload array directly with AdminPages instead of a simple callback function
  useEffect(() => {
    if (onRegisterAction) {
      onRegisterAction(compiledIssues);
    }

    return () => {
      if (onRegisterAction) onRegisterAction(null);
    };
  }, [onRegisterAction, compiledIssues]);

  const translateToEnglishTime = (timeStr) => {
    if (!timeStr) return "-";
    let time = timeStr.toLowerCase();
    if (time.includes("pg")) return time.replace("pg", "AM").toUpperCase();
    else if (time.includes("ptg") || time.includes("pt") || time.includes("mlm")) return time.replace(/ptg|pt|mlm/, "PM").toUpperCase();
    return timeStr.toUpperCase();
  };

  const buildHierarchy = useCallback((items, query = "") => {
    if (!items || !Array.isArray(items)) return;

    const root = { _count: 0, children: {}, unknown: [] };
    const lowerQuery = query.toLowerCase().trim();

    const filteredItems = items.filter(item => {
      if (!item) return false; 
      if (!query) return true;
      return (
        item.fgNumber?.toString().toLowerCase().includes(lowerQuery) ||
        item.part?.toString().toLowerCase().includes(lowerQuery) ||
        item.date?.toString().includes(query)
      );
    });

    filteredItems.forEach(item => {
      if (!item) return; 

      if (!item.date) { 
        if (!root.unknown) root.unknown = [];
        root.unknown.push(item); 
        return; 
      }

      const parts = item.date.split("/").map(Number);

      if (parts.length < 3 || parts.some(isNaN)) { 
        if (!root.unknown) root.unknown = [];
        root.unknown.push(item); 
        return; 
      }
  
      const [d, m, y] = parts;
      const dateObj = new Date(y, m - 1, d);
      
      const year = y.toString(); 
      const month = dateObj.toLocaleString('en-MY', { month: 'long' });
      const dayOfMonth = dateObj.getDate();
      const weekIndex = Math.ceil(dayOfMonth / 7);
      const ordinals = ["", "1st", "2nd", "3rd", "4th", "5th", "6th"];
      const week = `${ordinals[weekIndex]} Week`;
      const day = dateObj.toLocaleDateString("en-GB", { day: 'numeric', month: 'long', year: 'numeric' });
      
      const itemHasIssue = item.hasAnyOpenIssue === true;

      if (!root.children[year]) {
        root.children[year] = { _count: 0, hasIssue: false, children: {} };
      }
      if (!root.children[year].children[month]) {
        root.children[year].children[month] = { _count: 0, hasIssue: false, children: {} };
      }
      if (!root.children[year].children[month].children[week]) {
        root.children[year].children[month].children[week] = { _count: 0, hasIssue: false, children: {} };
      }
      if (!root.children[year].children[month].children[week].children[day]) {
        root.children[year].children[month].children[week].children[day] = { _count: 0, hasIssue: false, items: [] };
      }

      if (itemHasIssue) {
        root.children[year].children[month].hasIssue = true;
        root.children[year].children[month].children[week].hasIssue = true;
        root.children[year].children[month].children[week].children[day].hasIssue = true;
      }

      root.children[year]._count++;
      root.children[year].children[month]._count++;
      root.children[year].children[month].children[week]._count++;
      root.children[year].children[month].children[week].children[day]._count++;
      
      // FIXED: Added an explicit array fallback guard check here
      if (!root.children[year].children[month].children[week].children[day].items) {
        root.children[year].children[month].children[week].children[day].items = [];
      }
      root.children[year].children[month].children[week].children[day].items.push(item);
    });

    const finalData = { ...root.children };
    
    if (root.unknown && root.unknown.length > 0) {
      const unknownHasIssue = root.unknown.some(i => i?.hasAnyOpenIssue);
      finalData["Unknown Date"] = { 
        _count: root.unknown.length, 
        items: root.unknown,
        hasIssue: unknownHasIssue 
      };
    }

    setNestedData(finalData);

    if (query && filteredItems.length > 0) {
      const allKeys = new Set();
      let firstLeaf = null;
      const expandAndFindFirst = (data, path = "") => {
        if (!data) return;
        const keys = Object.keys(data).sort((a, b) => {
          return sortLatest ? b.localeCompare(a, undefined, {numeric: true}) : a.localeCompare(b, undefined, {numeric: true});
        });
        keys.forEach(key => {
          const p = path ? `${path}-${key}` : key;
          allKeys.add(p);
          if (data[key].items && !firstLeaf) firstLeaf = { title: key, items: data[key].items, path: p };
          if (data[key].children) expandAndFindFirst(data[key].children, p);
        });
      };
      expandAndFindFirst(finalData);
      setExpandedKeys(allKeys);
      if (firstLeaf) { setSelectedLeaf(firstLeaf); setActiveKey(firstLeaf.path); }
    } else if (query && filteredItems.length === 0) {
      setSelectedLeaf(null);
    }
  }, [sortLatest, setNestedData, setExpandedKeys, setSelectedLeaf, setActiveKey]);

  const fetchOlderDataInBackground = useCallback(async (fgMap) => {
    try {
      if (typeof setLoadingOlder === 'function') setLoadingOlder(true);
      const nodeSnaps = await Promise.all(NODES.map(node => get(ref(rtdb, node))));
      
      nodeSnaps.forEach((snap, index) => {
        const nodeName = NODES[index];
        snap.forEach(child => {
          const data = child.val();
          if (!data || !data.fgNumber) return;
          
          const fgItem = fgMap[data.fgNumber]; 
          if (!fgItem) return;

          if (!fgItem.nodeRemarks) fgItem.nodeRemarks = {};
          fgItem.nodeRemarks[nodeName] = { ...data, hasOpenIssue: data?.issue?.status === "open" };
          
          // FIXED: Ensured steps array exists cleanly before doing include validation or push execution
          if (!fgItem.steps) fgItem.steps = [];
          if (!fgItem.steps.includes(nodeName)) fgItem.steps.push(nodeName);
          
          fgItem.percentage = Math.round((fgItem.steps.length * 100) / NODES.length);
        });
      });

      Object.values(fgMap).forEach(fgItem => {
        if (fgItem && fgItem.nodeRemarks) {
          fgItem.hasAnyOpenIssue = Object.values(fgItem.nodeRemarks).some(n => n?.hasOpenIssue);
        }
      });
      
      const updatedList = [...Object.values(fgMap)].sort((a, b) => (b._timestamp || 0) - (a._timestamp || 0));
      
      cacheRef.current = updatedList;
      setFgItems(updatedList);
      setCachedData(updatedList);
      
      buildHierarchy(updatedList, searchQuery);
      
      if (selectedLeaf) {
        setSelectedLeaf(prev => {
          if (!prev) return null;
          const freshItemsForLeaf = updatedList.filter(item => {
            if (!item || !item.date) return prev.title === "Unknown Date";
            const parts = item.date.split("/").map(Number);
            if (parts.length < 3 || parts.some(isNaN)) return prev.title === "Unknown Date";
            const [d, m, y] = parts;
            const dayStr = new Date(y, m - 1, d).toLocaleDateString("en-GB", { 
              day: 'numeric', month: 'long', year: 'numeric' 
            });
            return dayStr === prev.title;
          });
          return { ...prev, items: freshItemsForLeaf };
        });
      }

    } finally { 
      if (typeof setLoadingOlder === 'function') setLoadingOlder(false); 
    }
  }, [searchQuery, selectedLeaf, buildHierarchy, setCachedData, setFgItems, setLoadingOlder, setSelectedLeaf]);

  const fetchFgNumbers = useCallback(async (forceRefresh = false) => {
    if (hasLoadedOnce && !forceRefresh) return;
    if (typeof setLoading === 'function') setLoading(true);

    try {
      const snapshot = await get(ref(rtdb, "Progress")); 

      if (snapshot.exists()) {
        const list = [];
        const fgMap = {}; 

        snapshot.forEach(child => {
          const data = child.val();
          if (!data) return;
          
          const fgItem = {
            fgNumber: child.key,
            date: data.receivingDate || null,
            time: translateToEnglishTime(data.receivingTime),
            part: data.labelType || null,
            quantity: data.quantity || "0",
            steps: ["s1", "s2", "s3", "s4", "s5"].filter(s => data[s] === true).map((_, i) => NODES[i]), 
            percentage: 0, 
            nodeRemarks: {}, 
            _timestamp: getFullTimestamp(data.receivingDate, data.receivingTime)
          };
          fgMap[child.key] = fgItem;
          list.push(fgItem);
        });

        setFgItems(list);
        setHasLoadedOnce(true);
        buildHierarchy(list, searchQuery);

        await fetchOlderDataInBackground(fgMap);
      } else {
        setFgItems([]);
        setNestedData({});
        setSelectedLeaf(null);
      }
    } catch (err) {
      console.error("Firebase Fetch Error:", err);
    } finally {
      if (typeof setLoading === 'function') setLoading(false);
    }
  }, [hasLoadedOnce, searchQuery, buildHierarchy, fetchOlderDataInBackground, setFgItems, setHasLoadedOnce, setNestedData, setLoading, setSelectedLeaf]);

  useEffect(() => {
    if (!hasLoadedOnce) {
      fetchFgNumbers();
    } else if (cachedData && cachedData.length > 0) {
      setFgItems(cachedData);
      cacheRef.current = cachedData;
      buildHierarchy(cachedData, searchQuery);
    }
  }, [hasLoadedOnce, cachedData, searchQuery, buildHierarchy, fetchFgNumbers, setFgItems]);

  const handleSearch = () => buildHierarchy(fgItems, searchQuery);
  
  const resetSearch = () => { 
    setSearchQuery(""); 
    setSelectedLeaf(null);
    setLocalHighlightTarget(null);
    if (typeof setSelectedFG === 'function') setSelectedFG("");
    if (clearExpandedUIStates) {
      clearExpandedUIStates(); 
    } else {
      setExpandedKeys(new Set());
    }
    buildHierarchy(fgItems, ""); 
  };

  const traceHierarchyPath = useCallback((item) => {
    if (!item || !item.date) {
      return {
        pathsToExpand: [], 
        leafPath: "Unknown Date", 
        leafTitle: "Unknown Date"
      };
    }

    const parts = item.date.split("/").map(Number);

    if (parts.length < 3 || parts.some(isNaN)) {
      return {
        pathsToExpand: [], 
        leafPath: "Unknown Date", 
        leafTitle: "Unknown Date"
      };
    }

    const [d, m, y] = parts;
    const dateObj = new Date(y, m - 1, d);
    
    const year = y.toString();
    const month = dateObj.toLocaleString('en-MY', { month: 'long' });
    const dayOfMonth = dateObj.getDate();
    const weekIndex = Math.ceil(dayOfMonth / 7);
    const ordinals = ["", "1st", "2nd", "3rd", "4th", "5th", "6th"];
    const week = `${ordinals[weekIndex]} Week`;
    const day = dateObj.toLocaleDateString("en-GB", { day: 'numeric', month: 'long', year: 'numeric' });

    return {
      pathsToExpand: [year, `${year}-${month}`, `${year}-${month}-${week}`],
      leafPath: `${year}-${month}-${week}-${day}`,
      leafTitle: day
    };
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const urlPage = queryParams.get('page');
    const urlFG = queryParams.get('fg');

    if (urlPage === 'progress' && urlFG && urlFG !== selectedFG) {
      if (typeof setSelectedFG === 'function') {
        setSelectedFG(urlFG);
      }
    }
  }, [selectedFG, setSelectedFG]);

  // FIXED: Isolated effect that coordinates selection state without loop nesting
  useEffect(() => {
    if (!selectedFG || !fgItems || fgItems.length === 0 || Object.keys(nestedData || {}).length === 0) return;

    const targetItem = fgItems.find(item => item && item.fgNumber === selectedFG);
    if (!targetItem) return;

    const trace = traceHierarchyPath(targetItem);
    if (!trace) return;

    // 1. Expand the tree paths safely
    setExpandedKeys(prev => {
      const next = new Set(prev);
      trace.pathsToExpand.forEach(p => next.add(p));
      next.add(trace.leafPath);
      return next;
    });

    // 2. Set active leaf string key 
    setActiveKey(trace.leafPath);
    
    // 3. Filter leaf dataset without breaking rendering hooks references
    let freshItemsForLeaf;
    if (trace.leafTitle === "Unknown Date") {
      freshItemsForLeaf = fgItems.filter(item => {
        if (!item || !item.date) return true;
        const parts = item.date.split("/").map(Number);
        return parts.length < 3 || parts.some(isNaN);
      });
    } else {
      freshItemsForLeaf = fgItems.filter(item => {
        if (!item || !item.date) return false;
        const parts = item.date.split("/").map(Number);
        if (parts.length < 3 || parts.some(isNaN)) return false;
        const [d, m, y] = parts;
        const dayStr = new Date(y, m - 1, d).toLocaleDateString("en-GB", { 
          day: 'numeric', month: 'long', year: 'numeric' 
        });
        return dayStr === trace.leafTitle;
      });
    }

    setSelectedLeaf({
      title: trace.leafTitle,
      path: trace.leafPath,
      items: freshItemsForLeaf
    });

    setLocalHighlightTarget(selectedFG);
    if (typeof forceExpandCard === 'function') {
      forceExpandCard(selectedFG);
    }

    const scrollTimer = setTimeout(() => {
      const element = document.getElementById(`fg-card-${selectedFG}`) || 
                      document.querySelector(`[data-fg="${selectedFG}"]`) ||
                      document.getElementById(`fg-${selectedFG}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: "smooth", 
          block: "center" 
        });
        console.log(`🟢 Layout Swapped: Auto-scrolled to target element ID: fg-card-${selectedFG}`);
      } else {
        console.warn(`⏳ DOM Element not mounted yet for FG: ${selectedFG}. Retrying alignment...`);
        setTimeout(() => {
          const retryElement = document.getElementById(`fg-card-${selectedFG}`) || 
                               document.querySelector(`[data-fg="${selectedFG}"]`);
          if (retryElement) {
            retryElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 200);
      }
    }, 450); 

    return () => clearTimeout(scrollTimer);
  }, [selectedFG, fgItems, nestedData, traceHierarchyPath, setActiveKey, forceExpandCard, setExpandedKeys, setSelectedLeaf]);

  useEffect(() => {
    if (!selectedFG) return;
    
    console.log("=== 🛠️ NAVIGATION TRACER INITIALIZED ===");
    console.log("Selected FG Target:", selectedFG);
    console.log("Total items in fgItems:", fgItems?.length);
    console.log("Nested hierarchy data ready?:", Object.keys(nestedData || {}).length > 0);

    const target = fgItems?.find(item => item && item.fgNumber === selectedFG);
    console.log("Target Item found in dataset?:", !!target);

    if (target) {
      const trace = traceHierarchyPath(target);
      console.log("Calculated Sidebar Path String:", trace?.leafPath);
    }

    const timer = setTimeout(() => {
      const elementById = document.getElementById(`fg-card-${selectedFG}`);
      const elementByAttr = document.querySelector(`[data-fg="${selectedFG}"]`);
      const fallbackElement = document.getElementById(`fg-${selectedFG}`);
      
      console.log("DOM Search Results:");
      console.log(" -> By ID ('fg-card-...'):", !!elementById);
      console.log(" -> By Attribute ('data-fg'):", !!elementByAttr);
      console.log(" -> By Original ID ('fg-...'):", !!fallbackElement);
      console.log("=========================================");
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedFG, fgItems, nestedData, traceHierarchyPath]);

  const renderItemGroups = (items, currentPath) => {
    // 1. Immediate guard against missing or non-array inputs
    if (!items || !Array.isArray(items) || items.length === 0) return null;
    
    const grouped = items.reduce((acc, fg) => {
      // 2. Heavy defensive check to prevent runtime prototype crashes
      if (!acc) acc = {};
      if (!fg) return acc;
      
      const part = fg.part || "No Part";
      
      // 3. Ensure the object path array exists before pushing
      if (!acc[part]) {
        acc[part] = [];
      }
      
      // 4. Final array integrity validation check
      if (Array.isArray(acc[part])) {
        acc[part].push(fg);
      }
      
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([part, fgList]) => {
        // Extra safe check to avoid mapping over empty properties if acc mutation faltered
        if (!Array.isArray(fgList)) return null;

        return (
          <div key={`${currentPath}-${part}`} className="part-section">
            <div className="part-header">
              <span>{part}</span>
              <span className="part-count">{fgList.length}</span>
            </div>
            <div className="items-container">
              {fgList
                .filter(Boolean) // Filter out any rogue nulls/undefined values inside the list
                .sort((a, b) => (a._timestamp || 0) - (b._timestamp || 0))
                .map(fg => (
                  <FGItemCard 
                    key={fg.fgNumber} 
                    refreshData={() => fetchFgNumbers(true)} 
                    fg={{...fg, stepsList: (fg.steps || []).map(s => ({type: "step", step: s}))}} 
                    isHighlighted={fg.fgNumber === localHighlightTarget} 
                    onNavigate={onNavigate} 
                  />
                ))}
            </div>
          </div>
        );
      });
  };

  const renderHierarchy = (data, path = "") => {
    if (!data) return null;
    const monthMap = {"January":1,"February":2,"March":3,"April":4,"May":5,"June":6,"July":7,"August":8,"September":9,"October":10,"November":11,"December":12};
    const keys = Object.keys(data).sort((a, b) => {
      if (a === "Unknown Date") return 1;
      if (b === "Unknown Date") return -1;
      if (monthMap[a] && monthMap[b]) return sortLatest ? monthMap[b] - monthMap[a] : monthMap[a] - monthMap[b];
      if (a.includes("Week") && b.includes("Week")) {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        return sortLatest ? numB - numA : numA - numB;
      }
      return sortLatest ? b.localeCompare(a, undefined, {numeric: true}) : a.localeCompare(b, undefined, {numeric: true});
    });

    return keys.map(key => {
      const currentPath = path ? `${path}-${key}` : key;
      const node = data[key];
      if (!node) return null;
      
      const isExpanded = expandedKeys.has(currentPath);
      const isLeaf = !!node.items;

      const handleToggle = () => {
        setActiveKey(currentPath);
        const next = new Set(expandedKeys);
        
        if (isLeaf) {
          setSelectedLeaf({ title: key, items: node.items || [], path: currentPath });
          
          if (key === "Unknown Date") {
            if (next.has(currentPath)) {
              next.delete(currentPath);
            } else {
              next.add(currentPath);
            }
          }
        } else {
          const isYear = !path; 
          const isMonth = path && !path.includes("-");
          const isWeek = key.includes("Week");

          if (next.has(currentPath)) {
            next.delete(currentPath);
          } else {
            next.forEach(pathKey => {
              if (isYear) next.delete(pathKey);
              else if (isMonth || isWeek) {
                const parentOfOpen = pathKey.substring(0, pathKey.lastIndexOf("-"));
                if (parentOfOpen === path) next.delete(pathKey);
              }
            });
            next.add(currentPath);
          }
        }
        setExpandedKeys(next);
      };

      return (
        <div key={currentPath} className="hierarchy-node" style={{ marginLeft: path ? "16px" : "0" }}>
          <FGDateHeader 
            date={key} 
            count={node._count} 
            isExpanded={isExpanded} 
            isActive={activeKey === currentPath}
            onToggle={handleToggle} 
            hasIssue={node.hasIssue}
          />
          {isExpanded && !isLeaf && node.children && (
            <div className="group-content">
              {renderHierarchy(node.children, currentPath)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="admin-progress-root">
      {loading && (
        <div className="full-screen-loader">
          <div className="loader-content">
            <div className="main-spinner"></div>
            <p>Syncing Production Data...</p>
          </div>
        </div>
      )}

      <header className="progress-header">
        <div className="header-title-container">
          <h2>Production Progress</h2>
        </div>
        
        <div className="fg-search-row">
          <div className="search-input-wrapper">
            <input 
              type="text" 
              placeholder="Search FG, Part or Date..." 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
            {loadingOlder && <span className="sync-indicator spinning">🔄</span>}
          </div>
          
          <button onClick={handleSearch} className="button search-btn">Search</button>
          <button onClick={resetSearch} className="button reset-btn">Reset</button>
          
          <div className="divider" />
          
          <button 
            className="button refresh-btn" 
            onClick={() => fetchFgNumbers(true)} 
            disabled={loading || loadingOlder}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </header>

      <main className="desktop-layout">
        <aside className="desktop-sidebar">
          {renderHierarchy(nestedData)}
        </aside>
        <section className="desktop-main-content">
          {selectedLeaf ? (
            <div className="leaf-details">
              <h3 className="leaf-title">{selectedLeaf.title}</h3>
              <div className="leaf-grid">
                {renderItemGroups(selectedLeaf.items, selectedLeaf.path)}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📂</div>
              <p>Select a date from the sidebar to view detailed production items.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default AdminProgress;